/**
 * Copyright 2021-2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import {
  RenderingCondition,
  SectionRowElementBadge,
  SectionRowElementChart,
  SectionRowElementChartColorGradient,
  SectionRowElementTable,
  Stacklight,
  StacklightLamp,
  Template,
  Timeline,
  UaType,
  UaVariable,
  UaVariableEnum,
  UaVariableNumber,
} from '@umati/dashboard-types';
import _ from 'lodash';
import { Specifications } from 'Specifications';
import UnknownTypeTemplateJson from '../templates/_UnknownType.json';

interface TypeTemplateIndex {
  [key: string]: Template;
}

const typeTemplateIndex: TypeTemplateIndex = Specifications.flatMap((spec) => spec.TypeDefinitions)
  .filter((typeDef) => typeDef.Template)
  .reduce((map, typeDef) => {
    map[typeDef.Type] = typeDef.Template;
    return map;
  }, {});

export const DEFAULT_VALUE_UNDEFINED = 'Undefined';
const DEFAULT_VALUE_RANGE_MAX = 100;
const DEFAULT_VALUE_RANGE_MIN = 0;
const DEFAULT_PLACEHOLDER_OBJECTS_UNDEFINED = [];
const DEFAULT_BADGE_BACKGROUND_COLOR = '#343a40';
const DEFAULT_BADGE_TEXT_COLOR = '#FFFFFF';

const KEY_SECTION = 1000000000000;
const KEY_ROW = 2000000000000;
const KEY_ELEMENT = 3000000000000;

const KEY_SECTION_INDEX = 100000000000;
const KEY_SECTION_DATA_INDEX = 1000000000;
const KEY_ROW_INDEX = 10000000;
const KEY_ROW_DATA_INDEX = 100000;
const KEY_ELEMENT_INDEX = 1000;
const KEY_ELEMENT_DATA_INDEX = 10;

export default class TemplateHelper {
  static getTemplateByTypeId = (typeId: string): Template => {
    return typeTemplateIndex[typeId] || UnknownTypeTemplateJson;
  };

  static getTextValue = (text?: string): string => {
    if (text === null || text === undefined) {
      return DEFAULT_VALUE_UNDEFINED;
    }
    return text;
  };

  static getValue = (data: any, variable: string, defaultValue?: string): string => {
    if (!data || !variable) {
      return defaultValue || DEFAULT_VALUE_UNDEFINED;
    }

    const value = _.get(data, variable, defaultValue || DEFAULT_VALUE_UNDEFINED);
    if (value !== null && value !== undefined) {
      return value.toString();
    }
    return defaultValue || DEFAULT_VALUE_UNDEFINED;
  };

  static isNumericDataType = (dataType: string): boolean => {
    switch (dataType) {
      case 'Int16':
      case 'UInt16':
      case 'Int32':
      case 'UInt32':
      case 'Int64':
      case 'UInt64':
      case 'Float':
      case 'Double':
      case 'Number':
      case 'Integer':
      case 'UInteger':
      case 'Byte':
        return true;
      default:
        return false;
    }
  };

  static getVariableValue = (data: any, variable?: UaVariable, defaultValue?: string, customUnit?: string): string => {
    if (!data || !variable) {
      return defaultValue || DEFAULT_VALUE_UNDEFINED;
    }

    const variablePathSuffix = variable.hasProperties ? '.value' : '';

    if (variable.dataType === 'LocalizedText') {
      return TemplateHelper.getValue(data, variable.variablePath + variablePathSuffix + '.text');
    }

    if (variable.dataType === 'UtcTime') {
      const value = TemplateHelper.getValue(data, variable.variablePath + variablePathSuffix);
      return TemplateHelper.formatUtcTime(value);
    }

    if (TemplateHelper.isNumericDataType(variable.dataType)) {
      const variableNumber = variable as UaVariableNumber;
      if (variableNumber.hasUnit || customUnit) {
        const value = TemplateHelper.getValue(data, variable.variablePath + variablePathSuffix);
        const unit = TemplateHelper.getVariableUnit(data, variable, customUnit);
        if (!unit) {
          return value;
        }
        return value + ' ' + unit;
      }
    }

    if (variable.dataType === 'Enum') {
      const value = TemplateHelper.getValue(data, variable.variablePath + variablePathSuffix);
      const variableEnum = variable as UaVariableEnum;
      if (variableEnum.enumValues[value]) {
        return variableEnum.enumValues[value];
      }
      return value;
    }

    return TemplateHelper.getValue(data, variable.variablePath + variablePathSuffix, defaultValue);
  };

  static formatUtcTime = (utcTimeValue: string): string => {
    if (!utcTimeValue) {
      return DEFAULT_VALUE_UNDEFINED;
    }

    // Identify the parts of the date value
    // Example of OPC UAs UtcTime: 2022-4-1T9:53:31:11Z
    // Note: new Date(utcTimeValue) does not work because of missing leading zeros
    const dateAndTimeParts = utcTimeValue.split('T');
    if (dateAndTimeParts.length !== 2) {
      return utcTimeValue;
    }

    const dateParts = dateAndTimeParts[0].split('-');
    if (dateParts.length !== 3) {
      return utcTimeValue;
    }
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // zero-based index for months
    const day = parseInt(dateParts[2]);

    const timeParts = dateAndTimeParts[1].replace('Z', '').split(':');
    if (timeParts.length < 3) {
      return utcTimeValue;
    }
    const hour = parseInt(timeParts[0]);
    const minute = parseInt(timeParts[1]);
    const second = parseInt(timeParts[2]);

    return new Date(year, month, day, hour, minute, second).toLocaleString();
  };

  static getVariableValueAray = (data: any, variable?: UaVariable): string[] => {
    if (!data || !variable) {
      return [];
    }

    const variablePathSuffix = variable.hasProperties ? '.value' : '';
    const resolvedData = _.get(data, variable.variablePath + variablePathSuffix, []);

    const array: string[] = [];
    if (Array.isArray(resolvedData)) {
      _.forEach(resolvedData, function (value) {
        array.push(_.toString(value));
      });
    } else {
      array.push(_.toString(resolvedData));
    }

    return array;
  };

  static getVariableUnit = (data: any, variable?: UaVariable, customUnit?: string): string | undefined => {
    if (customUnit) {
      return customUnit;
    }

    if (!data || !variable) {
      return undefined;
    }

    const unit = TemplateHelper.getValue(data, variable.variablePath + '.properties.EngineeringUnits.DisplayName.text');
    if (unit === DEFAULT_VALUE_UNDEFINED) {
      return undefined;
    }
    return unit;
  };

  static getVariableRange = (
    data: any,
    defaultRange: number,
    variablePathSuffix: string,
    variable?: UaVariable,
    customRange?: number,
  ): number => {
    if (!data || !variable) {
      return defaultRange;
    }

    if (TemplateHelper.isNumericDataType(variable.dataType)) {
      if (customRange !== null && customRange !== undefined) {
        return customRange;
      }
      const variableNumber = variable as UaVariableNumber;
      if (variableNumber.hasRange) {
        const range = TemplateHelper.getValue(data, variable.variablePath + variablePathSuffix);
        if (range !== DEFAULT_VALUE_UNDEFINED) {
          return Number(range);
        }
      }
    }

    return defaultRange;
  };

  static getVariableRangeMax = (data: any, variable?: UaVariable, customRangeHigh?: number): number => {
    return TemplateHelper.getVariableRange(
      data,
      DEFAULT_VALUE_RANGE_MAX,
      '.properties.EURange.High',
      variable,
      customRangeHigh,
    );
  };

  static getVariableRangeMin = (data: any, variable?: UaVariable, customRangeLow?: number): number => {
    return TemplateHelper.getVariableRange(
      data,
      DEFAULT_VALUE_RANGE_MIN,
      '.properties.EURange.Low',
      variable,
      customRangeLow,
    );
  };

  static getChartAmount = (data: any, variable?: UaVariable): number => {
    if (!variable) {
      return 0;
    }
    const variablePathSuffix = variable.hasProperties ? '.value' : '';
    const variableValue = TemplateHelper.getValue(data, variable?.variablePath + variablePathSuffix);
    const variableValueNumber = Number(variableValue);
    return isNaN(variableValueNumber) ? 0 : variableValueNumber;
  };

  static getChartColorGradient = (
    element: SectionRowElementChart,
    value: number,
    data: any,
  ): SectionRowElementChartColorGradient | undefined => {
    if (element.conditionalColorGradients) {
      for (const conditionalColorGradient of element.conditionalColorGradients) {
        if (!conditionalColorGradient.comparisonValueVariable || !conditionalColorGradient.comparisonOperator) {
          continue;
        }

        const comparisonValue: number = TemplateHelper.getChartAmount(
          data,
          conditionalColorGradient.comparisonValueVariable,
        );
        if (
          (conditionalColorGradient.comparisonOperator == 'greaterThan' && value > comparisonValue) ||
          (conditionalColorGradient.comparisonOperator == 'greaterThanOrEqualTo' && value >= comparisonValue) ||
          (conditionalColorGradient.comparisonOperator == 'equalTo' && value == comparisonValue) ||
          (conditionalColorGradient.comparisonOperator == 'lessThanOrEqualTo' && value <= comparisonValue) ||
          (conditionalColorGradient.comparisonOperator == 'lessThan' && value < comparisonValue)
        ) {
          return {
            startColor: conditionalColorGradient.startColor,
            stopColor: conditionalColorGradient.stopColor,
          };
        }
      }
    }

    if (element.defaultColorGradient) {
      return {
        startColor: element.defaultColorGradient.startColor,
        stopColor: element.defaultColorGradient.stopColor,
      };
    }

    return undefined;
  };

  static getPlaceholderObjectsOfArray = (data: any[], variable: string, conditionType?: UaType): any[] => {
    if (!data || !variable) {
      return DEFAULT_PLACEHOLDER_OBJECTS_UNDEFINED;
    }

    const containsPoint = variable.includes('.');
    const index = variable.indexOf('.');
    const variableStart = containsPoint ? variable.substring(0, index) : variable;
    const variableEnd = containsPoint ? variable.substring(index + 1) : null;

    const array: any[] = [];
    data.forEach((dataItem) => {
      if (variableStart in dataItem) {
        const innerData = dataItem[variableStart];
        array.push(innerData);
      } else if (variableStart === '[PLACEHOLDER]') {
        for (const propertyName in dataItem) {
          const dataObject = dataItem[propertyName];
          if (conditionType) {
            const typeDefinition = dataObject.$TypeDefinition;
            if (
              typeDefinition &&
              (typeDefinition !== conditionType.typeDefinition || conditionType.subTypes.includes(typeDefinition))
            ) {
              continue;
            }
          }
          if (!('baseName' in dataObject)) {
            dataObject.baseName = propertyName;
          }
          array.push(dataObject);
        }
      }
    });

    if (variableEnd != null) {
      return TemplateHelper.getPlaceholderObjectsOfArray(array, variableEnd, conditionType);
    } else {
      return array;
    }
  };

  static getPlaceholderObjects = (data: any, variable?: string, conditionType?: UaType): any[] => {
    if (!data || !variable) {
      return DEFAULT_PLACEHOLDER_OBJECTS_UNDEFINED;
    }
    const dataArray: any[] = [];
    dataArray.push(data);
    return TemplateHelper.getPlaceholderObjectsOfArray(dataArray, variable, conditionType);
  };

  static getStacklightLamps = (data: any, stacklight?: Stacklight): StacklightLamp[] | undefined => {
    if (!stacklight) {
      return undefined;
    }

    const lampDataArray: any[] = [];
    if (stacklight.lampsPlaceholderObject !== null && stacklight.lampsPlaceholderObject !== undefined) {
      const placeholderData: any[] = TemplateHelper.getPlaceholderObjects(data, stacklight.lampsPlaceholderObject);
      placeholderData.forEach((dataItem) => {
        lampDataArray.push(dataItem);
      });
    } else {
      lampDataArray.push(data);
    }

    if (lampDataArray.length < 1) {
      return undefined;
    }

    const lamps: StacklightLamp[] = [];

    lampDataArray.forEach((lampData, lampDataIndex) => {
      const signalColorValue = TemplateHelper.getVariableValue(lampData, stacklight.colorVariable);
      const signalColor = stacklight.colorValues[signalColorValue] || 'off';

      let signalOn: boolean = true;
      if (stacklight.signalVariable !== null && stacklight.signalVariable !== undefined) {
        const signalValue = TemplateHelper.getVariableValue(lampData, stacklight.signalVariable);
        if (signalValue === stacklight.signalOnValue) {
          signalOn = true;
        }
        if (signalValue === stacklight.signalOffValue) {
          signalOn = false;
        }
      }

      let numberInList: number = lampDataIndex;
      const sortingValue = Number(TemplateHelper.getVariableValue(lampData, stacklight.sortingVariable));
      if (!isNaN(sortingValue)) {
        numberInList = sortingValue;
      }

      lamps.push({
        SignalColor: signalColor,
        SignalOn: signalOn,
        NumberInList: numberInList,
      });
    });

    return lamps;
  };

  static getTimelineColorMap = (timeline?: Timeline): { [key: string]: string } => {
    const colors: { [key: string]: string } = {};

    if (!timeline) {
      return colors;
    }

    for (const value in timeline.colorValues) {
      colors[value] = timeline.colorValues[value];
    }

    return colors;
  };

  static getBadgeBackgroundColor = (badgeElement: SectionRowElementBadge, variableValue: string): string => {
    for (const badgeValue of badgeElement.badgeValues) {
      if (badgeValue.value === variableValue) {
        return badgeValue.backgroundColor;
      }
    }
    return DEFAULT_BADGE_BACKGROUND_COLOR;
  };

  static getBadgeTextColor = (badgeElement: SectionRowElementBadge, variableValue: string): string => {
    for (const badgeValue of badgeElement.badgeValues) {
      if (badgeValue.value === variableValue) {
        return badgeValue.textColor;
      }
    }
    return DEFAULT_BADGE_TEXT_COLOR;
  };

  static getBadgeDisplayValue = (badgeElement: SectionRowElementBadge, variableValue: string): string => {
    for (const badgeValue of badgeElement.badgeValues) {
      if (badgeValue.value === variableValue) {
        return badgeValue.label || variableValue;
      }
    }
    return variableValue;
  };

  static getTableColumnHeaders = (tableElement: SectionRowElementTable): string[] => {
    return tableElement.columns.flatMap((column) => column.label);
  };

  static getTableData = (tableElement: SectionRowElementTable, data: any): any[][] => {
    const tableData: any[][] = [];
    for (const dataPlaceholderObject of TemplateHelper.getPlaceholderObjects(data, tableElement.rowPlaceholderObject)) {
      const tableRowData: any[] = [];
      for (const column of tableElement.columns) {
        tableRowData.push(TemplateHelper.getVariableValue(dataPlaceholderObject, column.valueVariable));
      }
      tableData.push(tableRowData);
    }
    return tableData;
  };

  static getKey = (
    sectionIndex: number,
    sectionDataIndex?: number,
    rowIndex?: number,
    rowDataIndex?: number,
    elementIndex?: number,
    elementDataIndex?: number,
  ): number => {
    return (
      KEY_SECTION_INDEX * sectionIndex +
      KEY_SECTION_DATA_INDEX * (sectionDataIndex || 0) +
      KEY_ROW_INDEX * (rowIndex || 0) +
      KEY_ROW_DATA_INDEX * (rowDataIndex || 0) +
      KEY_ELEMENT_INDEX * (elementIndex || 0) +
      KEY_ELEMENT_DATA_INDEX * (elementDataIndex || 0)
    );
  };

  static getKeySection = (sectionIndex: number, sectionDataIndex?: number): number => {
    return TemplateHelper.getKey(sectionIndex, sectionDataIndex) + KEY_SECTION;
  };

  static getKeyRow = (
    sectionIndex: number,
    sectionDataIndex?: number,
    rowIndex?: number,
    rowDataIndex?: number,
  ): number => {
    return TemplateHelper.getKey(sectionIndex, sectionDataIndex, rowIndex, rowDataIndex) + KEY_ROW;
  };

  static getKeyElement = (
    sectionIndex: number,
    sectionDataIndex?: number,
    rowIndex?: number,
    rowDataIndex?: number,
    elementIndex?: number,
    elementDataIndex?: number,
  ): number => {
    return (
      TemplateHelper.getKey(sectionIndex, sectionDataIndex, rowIndex, rowDataIndex, elementIndex, elementDataIndex) +
      KEY_ELEMENT
    );
  };

  static checkRenderingCondition = (data: any, condition?: RenderingCondition): boolean => {
    if (!condition || !condition.values) {
      return true;
    }
    const value = TemplateHelper.getVariableValue(data, condition.valueVariable);
    return condition.values.includes(value);
  };
}
